import { FC, useMemo } from 'react';
import { BodyText, Box, Card, FileConversionConfiguration, FileConversionService, useForm, withStyles } from 'vopay';
import { CircleButton, Button } from 'UI';
import { Dialog } from '@/providers/Dialog.provider';
import styles from './DownloadFileDialog.module.scss';
import { useTranslation } from 'react-i18next';
import downloadFile from '../svgs/download_file.svg';
import { useDownloadFileFormFields, type DownloadFileFormValues } from './useDownloadFileFormFields';

type DownloadFileDialogProps = {
  onCancel: () => void;
  onConfirm: (values: DownloadFileFormValues) => void;
  configurations: FileConversionConfiguration[];
  configurationsLoading: boolean;
  service: FileConversionService;
};

const cn = withStyles(styles);

export type ProcessFileImgs = 'domainSetup';

const DownloadFileDialog: FC<DownloadFileDialogProps> = ({
  onCancel,
  onConfirm,
  configurations,
  configurationsLoading,
  service,
}) => {
  const { t } = useTranslation('translations', { keyPrefix: 'FileUpload.History.FileConversionDialog' });
  const { convertTo } = useDownloadFileFormFields({ configurations, configurationsLoading });

  // Auto-select default processor if it's the only one available
  const defaultProcessorId = useMemo(() => {
    if (configurations.length === 1 && configurations[0].Processor === 'default_processor') {
      return configurations[0].FileConversionConfigurationID;
    }

    return '';
  }, [configurations]);

  const initialValues: DownloadFileFormValues = {
    Service: service,
    PaymentRail: '',
    FileConversionConfigurationID: defaultProcessorId,
    ValueDate: new Date().getTime().toString(),
    FallbackPaymentRail: '',
  };

  const { renderFormElements, ctx } = useForm<DownloadFileFormValues>({
    initialValues,
    config: convertTo,
    onSubmit: (values, ctx) => {
      onConfirm({
        ...values,
        FileConversionConfigurationID: ctx.values.FileConversionConfigurationID,
      });
    },
  });

  return (
    <Card className={cn('modalContainer')}>
      <form onSubmit={ctx.handleSubmit}>
        <div className={cn('header')}>
          <img src={downloadFile} alt={'download file'} className={cn('icon')} />
          <Dialog.Title level="4">{t('title', { defaultValue: 'Download File' })}</Dialog.Title>

          <div className={cn('closeButton')}>
            <CircleButton icon="close" size="large" circleFill onClick={onCancel} />
          </div>

          <BodyText className={cn('description')}>
            {t('desc', {
              defaultValue: `Please select the processor`,
            })}
          </BodyText>
        </div>

        <Box flx="col" gap="common">
          {renderFormElements({ config: convertTo })}

          <Button type="submit" className={cn('button')} colorScheme="primary">
            {t('download', { defaultValue: 'Download' })}
          </Button>
        </Box>
      </form>
    </Card>
  );
};

export default DownloadFileDialog;
